import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, Calendar, ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import SEO from "../components/SEO";
import CTASection from "../components/sections/CTASection";
import { getBlogPost } from "../lib/api";
import { getAuthor } from "../data/authors";

function renderMarkdown(md) {
  if (!md) return "";
  // Lightweight markdown -> HTML for our prose
  let html = md
    .replace(/^### (.*$)/gim, "<h3>$1</h3>")
    .replace(/^## (.*$)/gim, "<h2>$1</h2>")
    .replace(/^# (.*$)/gim, "<h2>$1</h2>")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    // Images: ![alt](url)  →  <img alt="alt" src="url" loading="lazy" />
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img alt="$1" src="$2" loading="lazy" class="rounded-lg my-4" />')
    // Links: [text](url)  →  <a href="url">text</a>. External URLs open in new tab.
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text, url) => {
      const isExternal = /^https?:\/\//i.test(url);
      const attrs = isExternal
        ? ` href="${url}" target="_blank" rel="noopener noreferrer"`
        : ` href="${url}"`;
      return `<a${attrs} class="text-forest underline hover:text-forest-dark">${text}</a>`;
    });

  // Tables
  const lines = html.split("\n");
  const out = [];
  let inList = null; let inTable = false; let tableRows = [];
  for (let line of lines) {
    if (line.match(/^\|.*\|$/)) {
      if (!inTable) { inTable = true; tableRows = []; }
      tableRows.push(line);
      continue;
    }
    if (inTable) {
      const t = ["<table>"];
      tableRows.forEach((r, i) => {
        if (r.match(/^\|[\s|:-]+\|$/)) return;
        const cells = r.split("|").slice(1, -1).map((c) => c.trim());
        const tag = i === 0 ? "th" : "td";
        t.push("<tr>" + cells.map((c) => `<${tag}>${c}</${tag}>`).join("") + "</tr>");
      });
      t.push("</table>");
      out.push(t.join(""));
      inTable = false; tableRows = [];
    }
    if (/^\d+\.\s/.test(line)) {
      if (inList !== "ol") { if (inList) out.push(`</${inList}>`); out.push("<ol>"); inList = "ol"; }
      out.push("<li>" + line.replace(/^\d+\.\s/, "") + "</li>");
    } else if (/^-\s/.test(line)) {
      if (inList !== "ul") { if (inList) out.push(`</${inList}>`); out.push("<ul>"); inList = "ul"; }
      out.push("<li>" + line.replace(/^-\s/, "") + "</li>");
    } else {
      if (inList) { out.push(`</${inList}>`); inList = null; }
      if (line.trim() === "") out.push("");
      else if (line.startsWith("<h")) out.push(line);
      else out.push("<p>" + line + "</p>");
    }
  }
  if (inList) out.push(`</${inList}>`);
  return out.join("\n");
}

export default function BlogPost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    getBlogPost(slug).then((d) => {
      // Consolidation redirect handling: the API returns HTTP 301 to the
      // survivor's /api/blog/posts/{new-slug} URL when the requested slug
      // has a redirect_to. Axios transparently follows the redirect and we
      // receive the survivor's payload here. If the returned post.slug
      // differs from the URL slug, correct the address bar so users and
      // Googlebot land on the canonical survivor URL.
      if (d && d.post && d.post.slug && d.post.slug !== slug) {
        window.location.replace(`/blog/${d.post.slug}`);
        return;
      }
      setData(d);
    }).catch(() => setData(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen py-32 text-center text-charcoal-soft" aria-busy="true">Loading...</div>;
  if (!data) return (
    <>
      {/*
        Soft-404 guard: mark this SPA fallback view as noindex so that any
        stale, deleted, or misspelled /blog/{slug} URL that reaches the client
        stops being bucketed as "Crawled - currently not indexed" or Soft-404
        by search engines. See /app/memory/gsc_index_loss_interim_report.md.
      */}
      <SEO noindex title="Post not found" description="The blog post you requested could not be found on Ohio Accident Aid." />
      <div className="min-h-screen py-32 text-center" data-testid="blog-post-not-found">
        <h1 className="text-3xl font-display font-bold text-forest mb-3">Post not found</h1>
        <Link to="/blog" className="btn-primary">Back to blog</Link>
      </div>
    </>
  );

  const { post, related } = data;
  const html = renderMarkdown(post.content);
  const faqs = Array.isArray(post.faq_items) ? post.faq_items.filter((f) => f.question && f.answer) : [];

  // Author schema resolution.
  //
  // Backwards-compatible: articles whose `author` field still reads
  // "Ohio Accident Aid Team" (or any string not present in the AUTHORS
  // registry) continue to emit Organization-typed author schema, exactly as
  // before. This is Stage A infrastructure — no article records have been
  // migrated yet. When an article's author field is updated to a registered
  // Person (e.g. "Aaron Bankhurst"), the byline becomes a link to the
  // author profile and the JSON-LD emits Person + url.
  const knownAuthor = getAuthor(post.author);
  const authorSchema = knownAuthor
    ? {
        "@type": "Person",
        name: knownAuthor.name,
        url: knownAuthor.profileUrl,
      }
    : { "@type": "Organization", name: post.author || "Ohio Accident Aid" };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.seo_title || post.title,
    "description": post.seo_description || post.excerpt,
    "image": post.cover_image
      ? (post.cover_image.startsWith("http") ? post.cover_image : `${process.env.REACT_APP_BACKEND_URL}${post.cover_image}`)
      : undefined,
    "datePublished": post.created_at,
    "dateModified": post.updated_at || post.created_at,
    "author": authorSchema,
    "publisher": { "@type": "Organization", "name": "Ohio Accident Aid" },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://ohioaccidentaid.com/blog/${post.slug}`,
    },
  };

  const faqSchema = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer },
    })),
  } : null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ohioaccidentaid.com/" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://ohioaccidentaid.com/blog" },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://ohioaccidentaid.com/blog/${post.slug}` },
    ],
  };

  return (
    <>
      <SEO
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt}
        type="article"
        image={post.cover_image
          ? (post.cover_image.startsWith("http") ? post.cover_image : `${process.env.REACT_APP_BACKEND_URL}${post.cover_image}`)
          : undefined}
        url={post.canonical_url || `https://ohioaccidentaid.com/blog/${post.slug}`}
        imageAlt={post.featured_image_alt || post.title}
        schema={articleSchema}
        extraSchemas={faqSchema ? [faqSchema, breadcrumbSchema] : [breadcrumbSchema]}
      />
      <article className="pt-12 md:pt-16 pb-16 px-6 bg-bone">
        <div className="max-w-3xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-charcoal-soft hover:text-terracotta mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta mb-4">{post.category}</div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-forest tracking-tight mb-5 text-balance leading-[1.05]">
            {post.title}
          </h1>
          <p className="text-lg md:text-xl text-charcoal-soft leading-relaxed mb-6">{post.excerpt}</p>
          <div className="flex items-center gap-4 text-sm text-charcoal-soft border-y border-black/5 py-4 mb-10">
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {post.read_time} min read</span>
            <span>•</span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </span>
            <span>•</span>
            {knownAuthor ? (
              <span data-testid="post-byline">
                By{" "}
                <Link
                  to={`/authors/${knownAuthor.slug}`}
                  className="font-semibold text-forest hover:text-terracotta underline"
                  data-testid="post-byline-link"
                >
                  {knownAuthor.name}
                </Link>
              </span>
            ) : (
              <span data-testid="post-byline">By {post.author}</span>
            )}
          </div>

          {post.cover_image && (
            <img
              src={post.cover_image.startsWith("http") ? post.cover_image : `${process.env.REACT_APP_BACKEND_URL}${post.cover_image}`}
              alt={post.title}
              width="1600"
              height="900"
              className="w-full rounded-2xl mb-10 border border-black/5"
              style={{ aspectRatio: "16 / 9", height: "auto" }}
              loading="eager"
              fetchpriority="high"
              data-testid="post-cover-image"
            />
          )}

          <div className="prose-content" dangerouslySetInnerHTML={{ __html: html }} />

          {faqs.length > 0 && (
            <section className="mt-12 pt-10 border-t border-black/10" data-testid="post-faq-section">
              <h2 className="text-3xl font-display font-bold text-forest mb-6 tracking-tight">Frequently asked questions</h2>
              <div className="space-y-3">
                {faqs.map((f, i) => (
                  <details
                    key={i}
                    className="group bg-white border border-black/5 rounded-xl p-5 open:shadow-sm transition"
                    data-testid={`post-faq-${i}`}
                  >
                    <summary className="flex items-start justify-between gap-3 cursor-pointer list-none">
                      <span className="font-display font-bold text-forest text-base md:text-lg pr-3">{f.question}</span>
                      <ChevronDown className="w-5 h-5 text-charcoal-soft shrink-0 mt-1 group-open:rotate-180 transition-transform" />
                    </summary>
                    <p className="text-charcoal-soft text-sm md:text-base leading-relaxed mt-3 whitespace-pre-line">
                      {f.answer}
                    </p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      {related?.length > 0 && (
        <section className="px-6 py-16 bg-bone-alt">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-forest mb-8 tracking-tight">Related articles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.slice(0, 6).map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="card-elevated p-6 group"
                  data-testid={`related-post-${p.slug}`}
                >
                  <div className="text-[10px] font-bold tracking-wider uppercase text-terracotta mb-3">{p.category}</div>
                  <h3 className="font-display font-bold text-xl text-forest mb-3 leading-tight group-hover:text-terracotta transition-colors">{p.title}</h3>
                  <p className="text-sm text-charcoal-soft leading-relaxed mb-3">{p.excerpt}</p>
                  <div className="flex items-center gap-2 text-terracotta text-sm font-bold group-hover:gap-3 transition-all">
                    Read article <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </>
  );
}
